import React, { useState, useRef, useEffect, useCallback } from 'react';

// Enhanced physics constants - moved outside component to avoid dependency issues
const PHYSICS = {
  FRICTION: 0.92,           // Air resistance
  SPRING_STIFFNESS: 0.15,   // Spring force when hitting boundaries
  MAX_VELOCITY: 6,          // Maximum velocity cap (reduced from 15)
  MIN_VELOCITY: 0.1,        // Minimum velocity to continue animation
  SENSITIVITY: 0.3,         // Mouse/touch sensitivity (reduced from 0.5)
  BOUNCE_DAMPING: 0.7,      // Bounce damping factor
  VELOCITY_SAMPLES: 5       // Number of velocity samples to average
};

const Skills = () => {
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [velocity, setVelocity] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  
  // Physics refs
  const lastX = useRef(0);
  const lastTime = useRef(Date.now());
  const animationRef = useRef();
  const velocityHistory = useRef([]);
  const dragStartTime = useRef(0);
  const dragStartRotation = useRef(0);

  const skills = [
    { name: "React & JavaScript", icon: "⚛️", description: "Frontend Development", iconClass: "fab fa-react" },
    { name: "Python & Java", icon: "🐍", description: "Backend Languages", iconClass: "fab fa-python" },
    { name: "DevOps & CI/CD", icon: "🔄", description: "Automation", iconClass: "fas fa-cogs" },
    { name: "Docker & Kubernetes", icon: "🐳", description: "Containerization", iconClass: "fab fa-docker" },
    { name: "AWS & Cloud", icon: "☁️", description: "Cloud Infrastructure", iconClass: "fab fa-aws" },
    { name: "Spring Boot", icon: "🍃", description: "Java Framework", iconClass: "fas fa-leaf" },
    { name: "Database Design", icon: "🗄️", description: "Data Management", iconClass: "fas fa-database" },
    { name: "System Admin", icon: "⚙️", description: "Infrastructure", iconClass: "fas fa-server" },
    { name: "Node.js & Express", icon: "🟢", description: "Server Development", iconClass: "fab fa-node-js" },
    { name: "TypeScript", icon: "📘", description: "Type Safety", iconClass: "fab fa-js-square" },
    { name: "MongoDB & SQL", icon: "🗃️", description: "Database Systems", iconClass: "fas fa-database" },
    { name: "Git & GitHub", icon: "📚", description: "Version Control", iconClass: "fab fa-github" },
    { name: "Linux & Shell", icon: "🐧", description: "System Operations", iconClass: "fab fa-linux" },
    { name: "REST APIs", icon: "🔗", description: "API Development", iconClass: "fas fa-code" },
    { name: "Microservices", icon: "🏗️", description: "Architecture", iconClass: "fas fa-microchip" },
    { name: "Security & Auth", icon: "🔐", description: "Cybersecurity", iconClass: "fas fa-shield-alt" }
  ];

  // Get responsive radius values based on screen size
  const getResponsiveRadius = () => {
    if (windowSize.width <= 480) {
      // Mobile - smaller, more compact
      return { radiusX: 200, radiusY: 35 };
    } else if (windowSize.width <= 768) {
      // Tablet - medium size
      return { radiusX: 300, radiusY: 50 };
    } else if (windowSize.width <= 1024) {
      // Small desktop
      return { radiusX: 450, radiusY: 75 };
    } else {
      // Large desktop
      return { radiusX: 620, radiusY: 100 };
    }
  };

  // Get responsive skill count based on screen size
  const getResponsiveSkills = () => {
    if (windowSize.width <= 480) {
      // Mobile - show fewer skills for better performance
      return skills.slice(0, 8);
    } else if (windowSize.width <= 768) {
      // Tablet - show medium number of skills
      return skills.slice(0, 12);
    } else {
      // Desktop - show all skills
      return skills;
    }
  };

  // Get responsive physics settings based on screen size
  const getResponsivePhysics = () => {
    if (windowSize.width <= 480) {
      // Mobile - slower, more controlled
      return {
        ...PHYSICS,
        SENSITIVITY: 0.2,
        MAX_VELOCITY: 4,
        FRICTION: 0.95
      };
    } else if (windowSize.width <= 768) {
      // Tablet - medium settings
      return {
        ...PHYSICS,
        SENSITIVITY: 0.25,
        MAX_VELOCITY: 5,
        FRICTION: 0.93
      };
    } else {
      // Desktop - full settings
      return PHYSICS;
    }
  };

  // Calculate average velocity from recent samples
  const calculateAverageVelocity = useCallback((newVelocity) => {
    velocityHistory.current.push(newVelocity);
    if (velocityHistory.current.length > PHYSICS.VELOCITY_SAMPLES) {
      velocityHistory.current.shift();
    }
    return velocityHistory.current.reduce((sum, v) => sum + v, 0) / velocityHistory.current.length;
  }, []);

  // Enhanced momentum animation with spring physics
  const animateMomentum = useCallback((initialVelocity) => {
    const responsivePhysics = getResponsivePhysics();
    let v = Math.max(-responsivePhysics.MAX_VELOCITY, Math.min(responsivePhysics.MAX_VELOCITY, initialVelocity));
    let currentRotation = rotation;
    
    setIsAnimating(true);
    
    const animate = () => {
      if (Math.abs(v) > responsivePhysics.MIN_VELOCITY) {
        currentRotation += v;
        setRotation(currentRotation);
        
        // Apply friction
        v *= responsivePhysics.FRICTION;
        
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setVelocity(0);
      }
    };
    
    animate();
  }, [rotation, windowSize.width]);

  // Auto-rotation when not interacting
  useEffect(() => {
    if (!isDragging && !isAnimating) {
      const interval = setInterval(() => {
        setRotation(prev => prev + 0.3); // Slower auto-rotation for mobile
      }, 50);
      
      return () => clearInterval(interval);
    }
  }, [isDragging, isAnimating]);

  // Enhanced mouse/touch handlers
  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setIsAnimating(false);
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    lastX.current = clientX;
    lastTime.current = Date.now();
    dragStartTime.current = Date.now();
    dragStartRotation.current = rotation;
    
    // Clear velocity history
    velocityHistory.current = [];
    setVelocity(0);
    
    // Cancel any ongoing animations
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, [rotation]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    const responsivePhysics = getResponsivePhysics();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - lastX.current;
    const now = Date.now();
    const deltaTime = now - lastTime.current;
    
    if (deltaTime > 0) {
      const instantVelocity = (deltaX * responsivePhysics.SENSITIVITY) / deltaTime;
      const averageVelocity = calculateAverageVelocity(instantVelocity);
      
      setRotation(prev => prev - deltaX * responsivePhysics.SENSITIVITY);
      setVelocity(averageVelocity);
      
      lastX.current = clientX;
      lastTime.current = now;
    }
  }, [isDragging, calculateAverageVelocity, windowSize.width]);

  const onPointerUp = useCallback((e) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const responsivePhysics = getResponsivePhysics();
    
    // Calculate final velocity based on recent movement
    const dragDuration = Date.now() - dragStartTime.current;
    const dragDistance = rotation - dragStartRotation.current;
    
    let finalVelocity = velocity;
    
    // If we have a recent velocity, use it; otherwise calculate from drag
    if (Math.abs(velocity) < 0.1 && dragDuration > 0) {
      finalVelocity = (dragDistance * 60) / dragDuration; // Convert to degrees per second
    }
    
    // Apply momentum animation
    if (Math.abs(finalVelocity) > responsivePhysics.MIN_VELOCITY) {
      animateMomentum(finalVelocity);
    }
  }, [isDragging, velocity, rotation, animateMomentum, windowSize.width]);

  // Enhanced event listeners with passive: false for better touch handling
  useEffect(() => {
    const handlePointerMove = (e) => {
      if (isDragging) {
        onPointerMove(e);
      }
    };

    const handlePointerUp = (e) => {
      if (isDragging) {
        onPointerUp(e);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handlePointerMove, { passive: false });
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchend', handlePointerUp);
    }

    return () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, [isDragging, onPointerMove, onPointerUp]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div className="glass-pane">
        <div
          className="skills-carousel"
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            touchAction: 'none',
            // Mobile optimizations
            ...(windowSize.width <= 768 && {
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })
          }}
        >
          <div className="carousel-ring-oval">
            {getResponsiveSkills().map((skill, index) => {
              // Calculate the base angle for each skill
              const baseAngle = (index * 360) / getResponsiveSkills().length;
              // Add rotation to make skills move along the oval path
              const angle = baseAngle + rotation;
              const { radiusX, radiusY } = getResponsiveRadius();
              const x = Math.cos((angle * Math.PI) / 180) * radiusX;
              const y = Math.sin((angle * Math.PI) / 180) * radiusY;
              
              // Calculate Z position for front/back layering based on current position
              const z = Math.sin((angle * Math.PI) / 180) * 80;

              // Calculate opacity and scale based on Z position for smooth transitions
              const opacity = Math.max(0, Math.min(1, (z + 80) / 160));
              const scale = 0.7 + (opacity * 0.3);

              return (
                <div
                  key={index}
                  className="skill-item-oval"
                  style={{
                    transform: `translate(-50%, -50%) translate3d(${x}px, ${y}px, ${z}px) scale(${scale})`,
                    zIndex: Math.round(z),
                    opacity: opacity,
                    transition: isDragging ? 'none' : 'opacity 0.3s ease, transform 0.3s ease',
                    pointerEvents: isDragging ? 'none' : 'auto'
                  }}
                >
                  <div className="skill-card-oval">
                    <div className="skill-icon-oval"><i className={skill.iconClass}></i></div>
                    <div className="skill-name-oval">{skill.name}</div>
                    <div className="skill-description-oval">{skill.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="carousel-center-oval">
            <div className="center-content-oval">
              <span className="center-icon-oval"><i className="fas fa-laptop-code"></i></span>
              <span className="center-text-oval">Skills</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skills;